import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate } from "react-router";
import { ChevronDown } from "lucide-react";
import type { GeographicMarket } from "@runcheetah/shared";

import {
  CenteredLoader,
  EditableTagList,
  EditableTextarea,
  PageIntro,
  SegmentedFilter,
} from "@/components/common";
import { getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import {
  applyAudienceMarketCountryOption,
  applyAudienceMarketRegionOption,
  buildAudienceMarketsFromDrafts,
  createAudienceMarketDraft,
  formatAudienceMarketLabel,
  shouldHydrateAudienceMarketDrafts,
  toAudienceMarketDrafts,
  type AudienceMarketDraft,
} from "@/lib/audience-markets";
import { trpc } from "@/lib/trpc";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@runcheetah/ui";
import { toast } from "sonner";

type SaveState = "idle" | "saving" | "saved" | "error";
type PreferenceSectionKey = "general" | "audience";
type GeographyCountryOption = {
  countryCode: string;
  name: string;
  regionName: string | null;
  subregionName: string | null;
};
type GeographyRegionOption = {
  regionCode: string;
  name: string;
  kind: "region" | "subregion";
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="border-border/70 bg-card/90 shadow-sm">
      <CardHeader className="space-y-2 pb-4">
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">{children}</CardContent>
    </Card>
  );
}

export default function ContentPreferencesPage() {
  const intl = useIntl();
  const { brand, brandQuery } = useRouteBrand();
  const utils = trpc.useUtils();

  const voiceQuery = trpc.brand.getBrandVoice.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const profileQuery = trpc.brand.getBrandProfile.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const geographyOptionsQuery = trpc.brand.getGeographyOptions.useQuery(undefined, {
    enabled: Boolean(brand?.id),
    staleTime: 24 * 60 * 60 * 1000,
  });

  const updateBrandVoice = trpc.brand.updateBrandVoice.useMutation({
    onSuccess: async () => {
      if (!brand?.id) return;

      await Promise.all([
        utils.brand.getBrandVoice.invalidate({ brandId: brand.id }),
        utils.brand.getBrandProfile.invalidate({ brandId: brand.id }),
        utils.brand.getStrategyRegenerationStatus.invalidate({ brandId: brand.id }),
      ]);
    },
  });

  const [audienceDraft, setAudienceDraft] = useState("");
  const [psychographicsDraft, setPsychographicsDraft] = useState("");
  const [showAudienceMindset, setShowAudienceMindset] = useState(false);
  const [primaryAudienceSegmentsDraft, setPrimaryAudienceSegmentsDraft] = useState<string[]>([]);
  const [secondaryAudienceSegmentsDraft, setSecondaryAudienceSegmentsDraft] = useState<string[]>(
    [],
  );
  const [wordsToAvoidDraft, setWordsToAvoidDraft] = useState<string[]>([]);
  const [wordsToEmphasizeDraft, setWordsToEmphasizeDraft] = useState<string[]>([]);
  const [restrictedTopicsDraft, setRestrictedTopicsDraft] = useState<string[]>([]);
  const [marketDrafts, setMarketDrafts] = useState<AudienceMarketDraft[]>([]);
  const [marketError, setMarketError] = useState<string | null>(null);
  const [marketState, setMarketState] = useState<SaveState>("idle");
  const [activeSection, setActiveSection] = useState<PreferenceSectionKey>("general");
  const hydratedMarketBrandIdRef = useRef<string | null>(null);
  const isMarketEditorDirtyRef = useRef(false);

  const voice = voiceQuery.data;
  const profile = profileQuery.data;
  const geographyOptions = geographyOptionsQuery.data ?? { countries: [], regions: [] };

  useEffect(() => {
    setAudienceDraft(profile?.demographics ?? "");
  }, [profile?.demographics]);

  useEffect(() => {
    setPsychographicsDraft(profile?.psychographics ?? "");
    if (profile?.psychographics?.trim()) setShowAudienceMindset(true);
  }, [profile?.psychographics]);

  useEffect(() => {
    setPrimaryAudienceSegmentsDraft(profile?.primaryAudienceSegments ?? []);
  }, [profile?.primaryAudienceSegments]);

  useEffect(() => {
    setSecondaryAudienceSegmentsDraft(profile?.secondaryAudienceSegments ?? []);
  }, [profile?.secondaryAudienceSegments]);

  useEffect(() => {
    setWordsToAvoidDraft(voice?.wordsToAvoid ?? []);
  }, [voice?.wordsToAvoid]);

  useEffect(() => {
    setWordsToEmphasizeDraft(voice?.wordsToEmphasize ?? []);
  }, [voice?.wordsToEmphasize]);

  useEffect(() => {
    setRestrictedTopicsDraft(voice?.restrictedTopics ?? []);
  }, [voice?.restrictedTopics]);

  useEffect(() => {
    if (
      !shouldHydrateAudienceMarketDrafts({
        brandId: brand?.id,
        hydratedBrandId: hydratedMarketBrandIdRef.current,
        isDirty: isMarketEditorDirtyRef.current,
      }) ||
      !brand?.id
    ) {
      return;
    }

    hydratedMarketBrandIdRef.current = brand.id;
    isMarketEditorDirtyRef.current = false;
    setMarketDrafts(toAudienceMarketDrafts(profile?.geographicMarkets ?? []));
    setMarketError(null);
    setMarketState("idle");
  }, [brand?.id, profile?.geographicMarkets]);

  if (brandQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "contentPreferences.loading",
          defaultMessage: "Loading content preferences...",
        })}
      />
    );
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  if (voiceQuery.isPending || profileQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "contentPreferences.loading",
          defaultMessage: "Loading content preferences...",
        })}
      />
    );
  }

  const countryMarketCount = marketDrafts.filter(
    (market) => market.scope === "country" && market.countryCode.trim(),
  ).length;
  const saveVoiceField = (fieldPath: string, value: string | string[] | GeographicMarket[]) =>
    updateBrandVoice
      .mutateAsync({ brandId: brand.id, fields: { [fieldPath]: value } })
      .catch(() => {
        toast.error(
          intl.formatMessage({
            id: "common.couldNotSaveChanges",
            defaultMessage: "Could not save changes",
          }),
        );
        throw new Error("save_failed");
      });

  const handleSaveMarkets = async () => {
    let markets: GeographicMarket[];
    try {
      markets = buildAudienceMarketsFromDrafts(marketDrafts);
    } catch (error) {
      setMarketState("error");
      setMarketError(
        error instanceof Error
          ? error.message
          : intl.formatMessage({
              id: "contentPreferences.audienceMarkets.invalid",
              defaultMessage: "Check the audience market details and try again.",
            }),
      );
      return;
    }

    setMarketState("saving");
    setMarketError(null);
    try {
      await saveVoiceField("targetAudience.geographicMarkets", markets);
      isMarketEditorDirtyRef.current = false;
      hydratedMarketBrandIdRef.current = brand.id;
      setMarketDrafts(toAudienceMarketDrafts(markets));
      setMarketState("saved");
    } catch {
      setMarketState("error");
      setMarketError(
        intl.formatMessage({
          id: "common.couldNotSaveChanges",
          defaultMessage: "Could not save changes",
        }),
      );
    }
  };

  const preferenceSections = [
    {
      value: "general" as const,
      label: intl.formatMessage({
        id: "contentPreferences.sections.general",
        defaultMessage: "General",
      }),
    },
    {
      value: "audience" as const,
      label: intl.formatMessage({
        id: "contentPreferences.sections.audience",
        defaultMessage: "Audience",
      }),
    },
  ];

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <PageIntro
        title={intl.formatMessage({
          id: "contentPreferences.title",
          defaultMessage: "Content Preferences",
        })}
        titleClassName="text-balance"
      />

      <div className="space-y-4">
        <SegmentedFilter
          options={preferenceSections}
          value={activeSection}
          onChange={setActiveSection}
        />

        {activeSection === "general" ? (
          <Section
            title={intl.formatMessage({
              id: "contentPreferences.sections.general",
              defaultMessage: "General",
            })}
          >
            <div className="grid gap-5 lg:grid-cols-2">
              <EditableTagList
                label={intl.formatMessage({
                  id: "contentPreferences.wordsToAvoid.label",
                  defaultMessage: "Words to Avoid",
                })}
                description={intl.formatMessage({
                  id: "contentPreferences.wordsToAvoid.description",
                  defaultMessage:
                    "Words, phrases, or claims that feel off-brand, risky, or too generic.",
                })}
                tags={wordsToAvoidDraft}
                onSave={async (tags) => {
                  setWordsToAvoidDraft(tags);
                  await saveVoiceField("brandVoiceCommunicationStyle.wordsToAvoid", tags);
                }}
              />
              <EditableTagList
                label={intl.formatMessage({
                  id: "contentPreferences.wordsToEmphasize.label",
                  defaultMessage: "Words to Emphasize",
                })}
                description={intl.formatMessage({
                  id: "contentPreferences.wordsToEmphasize.description",
                  defaultMessage:
                    "Words and phrases that should show up more often in your messaging.",
                })}
                tags={wordsToEmphasizeDraft}
                onSave={async (tags) => {
                  setWordsToEmphasizeDraft(tags);
                  await saveVoiceField("brandVoiceCommunicationStyle.wordsToEmphasize", tags);
                }}
              />
            </div>

            <EditableTagList
              label={intl.formatMessage({
                id: "contentPreferences.restrictedTopics.label",
                defaultMessage: "Restricted Topics",
              })}
              description={intl.formatMessage({
                id: "contentPreferences.restrictedTopics.description",
                defaultMessage: "Topics the brand should avoid entirely or handle with extra care.",
              })}
              tags={restrictedTopicsDraft}
              onSave={async (tags) => {
                setRestrictedTopicsDraft(tags);
                await saveVoiceField("brandVoiceCommunicationStyle.restrictedTopics", tags);
              }}
              placeholder={intl.formatMessage({
                id: "contentPreferences.restrictedTopics.placeholder",
                defaultMessage: "Add a restricted topic",
              })}
            />
          </Section>
        ) : null}

        {activeSection === "audience" ? (
          <Section
            title={intl.formatMessage({
              id: "contentPreferences.sections.audience",
              defaultMessage: "Audience",
            })}
          >
            <EditableTextarea
              label={intl.formatMessage({
                id: "contentPreferences.audience.label",
                defaultMessage: "Who you're speaking to",
              })}
              description={intl.formatMessage({
                id: "contentPreferences.audience.description",
                defaultMessage:
                  "Describe the people your content needs to reach in practical, everyday terms.",
              })}
              value={audienceDraft}
              onChange={setAudienceDraft}
              onSave={async (value) => {
                setAudienceDraft(value);
                await saveVoiceField("targetAudience.demographics", value);
              }}
              placeholder={intl.formatMessage({
                id: "contentPreferences.audience.placeholder",
                defaultMessage: "Who are you trying to reach, and what context are they in?",
              })}
            />

            {showAudienceMindset ? (
              <EditableTextarea
                label={intl.formatMessage({
                  id: "contentPreferences.audienceMindset.label",
                  defaultMessage: "Audience mindset",
                })}
                description={intl.formatMessage({
                  id: "contentPreferences.audienceMindset.description",
                  defaultMessage:
                    "Capture what this audience cares about, what they are skeptical of, and what motivates them.",
                })}
                value={psychographicsDraft}
                onChange={setPsychographicsDraft}
                onSave={async (value) => {
                  setPsychographicsDraft(value);
                  await saveVoiceField("targetAudience.psychographics", value);
                }}
                placeholder={intl.formatMessage({
                  id: "contentPreferences.audienceMindset.placeholder",
                  defaultMessage: "What matters to them, and what influences their decisions?",
                })}
              />
            ) : (
              <Button type="button" variant="outline" onClick={() => setShowAudienceMindset(true)}>
                {intl.formatMessage({
                  id: "contentPreferences.audienceMindset.add",
                  defaultMessage: "Add audience mindset",
                })}
              </Button>
            )}

            <AudienceMarketsEditor
              drafts={marketDrafts}
              onChange={(nextDrafts) => {
                isMarketEditorDirtyRef.current = true;
                setMarketDrafts(nextDrafts);
                setMarketError(null);
                setMarketState("idle");
              }}
              onSave={handleSaveMarkets}
              state={marketState}
              error={marketError}
              hasCountryMarket={countryMarketCount > 0}
              countryOptions={geographyOptions.countries}
              regionOptions={geographyOptions.regions}
            />

            <div className="grid gap-5 lg:grid-cols-2">
              <EditableTagList
                label={intl.formatMessage({
                  id: "contentPreferences.primaryAudienceSegments.label",
                  defaultMessage: "Primary audience segments",
                })}
                description={intl.formatMessage({
                  id: "contentPreferences.primaryAudienceSegments.description",
                  defaultMessage: "The main segments your content should speak to first.",
                })}
                tags={primaryAudienceSegmentsDraft}
                onSave={async (tags) => {
                  setPrimaryAudienceSegmentsDraft(tags);
                  await saveVoiceField("targetAudience.primaryAudienceSegments", tags);
                }}
                placeholder={intl.formatMessage({
                  id: "contentPreferences.primaryAudienceSegments.placeholder",
                  defaultMessage: "Add a primary audience segment",
                })}
              />
              <EditableTagList
                label={intl.formatMessage({
                  id: "contentPreferences.secondaryAudienceSegments.label",
                  defaultMessage: "Secondary audience segments",
                })}
                description={intl.formatMessage({
                  id: "contentPreferences.secondaryAudienceSegments.description",
                  defaultMessage: "Additional segments that matter but are not the main focus.",
                })}
                tags={secondaryAudienceSegmentsDraft}
                onSave={async (tags) => {
                  setSecondaryAudienceSegmentsDraft(tags);
                  await saveVoiceField("targetAudience.secondaryAudienceSegments", tags);
                }}
                placeholder={intl.formatMessage({
                  id: "contentPreferences.secondaryAudienceSegments.placeholder",
                  defaultMessage: "Add a secondary audience segment",
                })}
              />
            </div>
          </Section>
        ) : null}
      </div>
    </div>
  );
}

function AudienceMarketsEditor({
  drafts,
  onChange,
  onSave,
  state,
  error,
  hasCountryMarket,
  countryOptions,
  regionOptions,
}: {
  drafts: AudienceMarketDraft[];
  onChange: (drafts: AudienceMarketDraft[]) => void;
  onSave: () => void;
  state: SaveState;
  error: string | null;
  hasCountryMarket: boolean;
  countryOptions: GeographyCountryOption[];
  regionOptions: GeographyRegionOption[];
}) {
  const intl = useIntl();
  const [areMarketsCollapsed, setAreMarketsCollapsed] = useState(false);
  const hasAppliedDefaultCollapseRef = useRef(false);

  useEffect(() => {
    if (hasAppliedDefaultCollapseRef.current || drafts.length === 0) return;

    hasAppliedDefaultCollapseRef.current = true;
    setAreMarketsCollapsed(drafts.length > 3);
  }, [drafts.length]);

  const updateDraft = (index: number, patch: Partial<AudienceMarketDraft>) => {
    onChange(
      drafts.map((draft, draftIndex) =>
        draftIndex === index ? normalizeDraftPatch({ draft, patch }) : draft,
      ),
    );
  };
  const removeDraft = (index: number) =>
    onChange(drafts.filter((_, draftIndex) => draftIndex !== index));

  return (
    <div className="space-y-3 rounded-2xl border border-border/70 bg-background/60 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            <FormattedMessage
              id="contentPreferences.audienceMarkets.label"
              defaultMessage="Audience markets"
            />
          </p>
          <p className="text-xs leading-5 text-muted-foreground">
            <FormattedMessage
              id="contentPreferences.audienceMarkets.description"
              defaultMessage="Set the countries or regions this brand actively serves. Country markets power local calendar events."
            />
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setAreMarketsCollapsed(false);
              onChange([createAudienceMarketDraft("country"), ...drafts]);
            }}
          >
            <FormattedMessage
              id="contentPreferences.audienceMarkets.addCountry"
              defaultMessage="Add country"
            />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setAreMarketsCollapsed(false);
              onChange([createAudienceMarketDraft("region"), ...drafts]);
            }}
          >
            <FormattedMessage
              id="contentPreferences.audienceMarkets.addRegion"
              defaultMessage="Add region"
            />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setAreMarketsCollapsed(false);
              onChange([createAudienceMarketDraft("global"), ...drafts]);
            }}
          >
            <FormattedMessage
              id="contentPreferences.audienceMarkets.addGlobal"
              defaultMessage="Use global"
            />
          </Button>
        </div>
      </div>

      <div
        className={
          hasCountryMarket
            ? "rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
            : "rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        }
      >
        {hasCountryMarket ? (
          <FormattedMessage
            id="contentPreferences.audienceMarkets.countryReady"
            defaultMessage="Calendar event markers can use these country markets."
          />
        ) : (
          <FormattedMessage
            id="contentPreferences.audienceMarkets.countryNeeded"
            defaultMessage="Add at least one country market, such as RO, to enable local calendar event markers."
          />
        )}
      </div>

      {drafts.length ? (
        <button
          type="button"
          onClick={() => setAreMarketsCollapsed((current) => !current)}
          className="inline-flex w-fit items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          aria-label={intl.formatMessage(
            areMarketsCollapsed
              ? {
                  id: "contentPreferences.audienceMarkets.expandMarkets",
                  defaultMessage: "Expand audience market details",
                }
              : {
                  id: "contentPreferences.audienceMarkets.collapseMarkets",
                  defaultMessage: "Collapse audience market details",
                },
          )}
          aria-expanded={!areMarketsCollapsed}
        >
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${areMarketsCollapsed ? "-rotate-90" : "rotate-0"}`}
          />
          <FormattedMessage
            id="contentPreferences.audienceMarkets.marketList"
            defaultMessage="Market details"
          />
          <span className="text-muted-foreground/70">
            <FormattedMessage
              id="contentPreferences.audienceMarkets.marketCount"
              defaultMessage="{count, plural, one {(# market)} other {(# markets)}}"
              values={{ count: drafts.length }}
            />
          </span>
        </button>
      ) : null}

      {!areMarketsCollapsed ? (
        drafts.length ? (
          <div className="space-y-3">
            {drafts.map((draft, index) => (
              <div key={`${draft.scope}-${index}`} className="rounded-xl border bg-card p-3">
                <div className="grid gap-3 lg:grid-cols-[9rem_minmax(14rem,1.3fr)_1.4fr_auto]">
                  <label className="space-y-1 text-xs font-medium text-muted-foreground">
                    <FormattedMessage
                      id="contentPreferences.audienceMarkets.scope"
                      defaultMessage="Scope"
                    />
                    <Select
                      value={draft.scope}
                      onValueChange={(value) =>
                        updateDraft(index, { scope: value as AudienceMarketDraft["scope"] })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="country">
                          <FormattedMessage
                            id="contentPreferences.audienceMarkets.scope.country"
                            defaultMessage="Country"
                          />
                        </SelectItem>
                        <SelectItem value="region">
                          <FormattedMessage
                            id="contentPreferences.audienceMarkets.scope.region"
                            defaultMessage="Region"
                          />
                        </SelectItem>
                        <SelectItem value="global">
                          <FormattedMessage
                            id="contentPreferences.audienceMarkets.scope.global"
                            defaultMessage="Global"
                          />
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </label>

                  <AudienceMarketLocationField
                    draft={draft}
                    countryOptions={countryOptions}
                    regionOptions={regionOptions}
                    onChange={(patch) => updateDraft(index, patch)}
                  />

                  <label className="space-y-1 text-xs font-medium text-muted-foreground">
                    <FormattedMessage
                      id="contentPreferences.audienceMarkets.rationale"
                      defaultMessage="Why this market?"
                    />
                    <Input
                      value={draft.rationale}
                      placeholder={intl.formatMessage({
                        id: "contentPreferences.audienceMarkets.rationalePlaceholder",
                        defaultMessage: "Manually set from customer context",
                      })}
                      onChange={(event) => updateDraft(index, { rationale: event.target.value })}
                    />
                  </label>

                  <div className="flex items-center justify-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDraft(index)}
                    >
                      <FormattedMessage id="common.remove" defaultMessage="Remove" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="rounded-xl border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
            <FormattedMessage
              id="contentPreferences.audienceMarkets.empty"
              defaultMessage="No audience markets are set yet. Add a country to make local events available in the calendar."
            />
          </p>
        )
      ) : null}

      {drafts.length ? (
        <div className="flex flex-wrap gap-2">
          {buildPreviewMarkets(drafts).map((market) => (
            <Badge
              key={`${market.scope}-${market.countryCode ?? market.regionCode ?? market.label}`}
              variant="outline"
            >
              {formatAudienceMarketLabel(market)}
            </Badge>
          ))}
        </div>
      ) : null}

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      <div className="flex items-center gap-3">
        <Button type="button" size="sm" onClick={onSave} disabled={state === "saving"}>
          {state === "saving" ? (
            <FormattedMessage id="common.saving" defaultMessage="Saving..." />
          ) : (
            <FormattedMessage
              id="contentPreferences.audienceMarkets.save"
              defaultMessage="Save markets"
            />
          )}
        </Button>
        {state === "saved" ? (
          <span className="text-xs text-muted-foreground">
            <FormattedMessage id="common.saved" defaultMessage="Saved" />
          </span>
        ) : null}
      </div>
    </div>
  );
}

function AudienceMarketLocationField({
  draft,
  countryOptions,
  regionOptions,
  onChange,
}: {
  draft: AudienceMarketDraft;
  countryOptions: GeographyCountryOption[];
  regionOptions: GeographyRegionOption[];
  onChange: (patch: Partial<AudienceMarketDraft>) => void;
}) {
  const intl = useIntl();

  if (draft.scope === "global") {
    return (
      <div className="space-y-1 text-xs font-medium text-muted-foreground">
        <FormattedMessage id="contentPreferences.audienceMarkets.market" defaultMessage="Market" />
        <div className="flex h-10 items-center rounded-md border border-dashed px-3 text-sm text-muted-foreground">
          <FormattedMessage
            id="contentPreferences.audienceMarkets.globalMarket"
            defaultMessage="Global audience"
          />
        </div>
      </div>
    );
  }

  if (draft.scope === "country") {
    const selectedCountry = countryOptions.find(
      (country) => country.countryCode === draft.countryCode,
    );

    return (
      <label className="space-y-1 text-xs font-medium text-muted-foreground">
        <FormattedMessage id="contentPreferences.audienceMarkets.market" defaultMessage="Market" />
        <Select
          value={
            selectedCountry?.countryCode ??
            (draft.countryCode.trim() ? draft.countryCode.trim().toUpperCase() : undefined)
          }
          onValueChange={(countryCode) => {
            const country = countryOptions.find((option) => option.countryCode === countryCode);
            onChange(country ? applyAudienceMarketCountryOption(draft, country) : { countryCode });
          }}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={intl.formatMessage({
                id: "contentPreferences.audienceMarkets.selectCountry",
                defaultMessage: "Select country",
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {selectedCountry ? null : draft.countryCode.trim() ? (
              <SelectItem value={draft.countryCode.trim().toUpperCase()}>
                {draft.label.trim() || draft.countryCode.trim().toUpperCase()}
              </SelectItem>
            ) : null}
            {countryOptions.map((country) => (
              <SelectItem key={country.countryCode} value={country.countryCode}>
                {country.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedCountry?.subregionName || selectedCountry?.regionName ? (
          <p className="text-[11px] font-normal text-muted-foreground/80">
            {[selectedCountry.subregionName, selectedCountry.regionName]
              .filter(Boolean)
              .join(" · ")}
          </p>
        ) : null}
      </label>
    );
  }

  const selectedRegion = regionOptions.find((region) => region.regionCode === draft.regionCode);

  return (
    <label className="space-y-1 text-xs font-medium text-muted-foreground">
      <FormattedMessage id="contentPreferences.audienceMarkets.market" defaultMessage="Market" />
      <Select
        value={
          selectedRegion?.regionCode ??
          (draft.regionCode.trim() ? draft.regionCode.trim().toUpperCase() : undefined)
        }
        onValueChange={(regionCode) => {
          const region = regionOptions.find((option) => option.regionCode === regionCode);
          onChange(region ? applyAudienceMarketRegionOption(draft, region) : { regionCode });
        }}
      >
        <SelectTrigger>
          <SelectValue
            placeholder={intl.formatMessage({
              id: "contentPreferences.audienceMarkets.selectRegion",
              defaultMessage: "Select region",
            })}
          />
        </SelectTrigger>
        <SelectContent>
          {selectedRegion ? null : draft.regionCode.trim() ? (
            <SelectItem value={draft.regionCode.trim().toUpperCase()}>
              {draft.label.trim() || draft.regionCode.trim().toUpperCase()}
            </SelectItem>
          ) : null}
          {regionOptions.map((region) => (
            <SelectItem key={`${region.kind}-${region.regionCode}`} value={region.regionCode}>
              {region.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selectedRegion ? (
        <p className="text-[11px] font-normal text-muted-foreground/80">
          {selectedRegion.kind === "subregion" ? (
            <FormattedMessage
              id="contentPreferences.audienceMarkets.subregionHint"
              defaultMessage="Subregion"
            />
          ) : (
            <FormattedMessage
              id="contentPreferences.audienceMarkets.regionHint"
              defaultMessage="Region"
            />
          )}
        </p>
      ) : null}
    </label>
  );
}

const normalizeDraftPatch = (input: {
  draft: AudienceMarketDraft;
  patch: Partial<AudienceMarketDraft>;
}): AudienceMarketDraft => {
  const next = { ...input.draft, ...input.patch };
  if (input.patch.scope === "global") {
    return { ...next, countryCode: "", regionCode: "", label: next.label || "Global" };
  }

  if (input.patch.scope === "country") {
    return { ...next, regionCode: "", label: input.draft.scope === "global" ? "" : next.label };
  }

  if (input.patch.scope === "region") {
    return { ...next, countryCode: "", label: input.draft.scope === "global" ? "" : next.label };
  }

  return next;
};

const buildPreviewMarkets = (drafts: AudienceMarketDraft[]): GeographicMarket[] => {
  try {
    return buildAudienceMarketsFromDrafts(drafts);
  } catch {
    return [];
  }
};
